/**
 * What to tell the user about their subscription, kept out of the components so each case can be
 * tested. Stripe's statuses are many; the user needs one sentence and, usually, one date.
 */

import type { Billing } from "./queries";

export type StatusLine =
  | { key: "none" }
  | { key: "trial"; date: string }
  | { key: "trialConverts"; date: string }
  | { key: "trialEnded"; date: string }
  | { key: "renews"; date: string }
  | { key: "ending"; date: string }
  | { key: "pastDue" }
  | { key: "lapsed" };

type Facts = Pick<
  Billing,
  "status" | "read_only" | "subscribed" | "trial_ends_at" | "current_period_end" | "cancel_at"
>;

export function statusLine(billing: Facts): StatusLine {
  const { status, trial_ends_at: trialEnd, current_period_end: periodEnd } = billing;
  if (status === "none") return { key: "none" };
  if (status === "trial_ended" && trialEnd) return { key: "trialEnded", date: trialEnd };
  if (billing.read_only) return { key: "lapsed" };
  if (status === "past_due") return { key: "pastDue" };
  // A cancellation outranks the trial: "ends on" is the date that matters now.
  if (billing.cancel_at) return { key: "ending", date: billing.cancel_at };
  if (status === "trialing" && trialEnd) {
    return billing.subscribed
      ? { key: "trialConverts", date: trialEnd }
      : { key: "trial", date: trialEnd };
  }
  if (periodEnd) return { key: "renews", date: periodEnd };
  return { key: "none" };
}

const DAY = 24 * 60 * 60 * 1000;

/** The calendar day a moment falls on in a zone, as that day's midnight UTC, for subtracting. */
function dayIn(moment: Date, timeZone: string): number {
  // en-CA formats as YYYY-MM-DD.
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(moment);
  return Date.parse(`${ymd}T00:00:00Z`);
}

/**
 * Calendar days from today to the day `iso` falls on, both in the workspace's zone: 0 on the last
 * day, 1 the day before. Counting days rather than hours keeps "ends in 4 days" agreeing with the
 * date the billing page prints, and with the count Stripe Checkout shows.
 */
export function daysLeft(iso: string, timeZone: string, now: Date = new Date()): number {
  return Math.max(0, Math.round((dayIn(new Date(iso), timeZone) - dayIn(now, timeZone)) / DAY));
}

/** The app shell starts counting down the app's own trial this many days before it ends. */
export const TRIAL_WARNING_DAYS = 7;

export type ShellNotice =
  | { kind: "readOnly"; trialEnd: string | null }
  | { kind: "pastDue" }
  | { kind: "photos"; count: number }
  | { kind: "trial"; days: number };

type Summary = {
  read_only: boolean;
  subscribed: boolean;
  subscription_status: string;
  trial_ends_at?: string | null;
  timezone: string;
  photos_awaiting_deletion?: number;
};

/**
 * The one billing notice the app shell shows above every page, if any. Read-only comes first
 * because it explains why changes fail. A failed payment comes next, since the plan is lost if the
 * card is not fixed. Then photos a downgrade left, waiting for a download before they go. Last,
 * the app's own trial is counted down in its final week; a trial with a card in Stripe converts
 * by itself and needs no reminder.
 */
export function shellNotice(workspace: Summary, now: Date = new Date()): ShellNotice | null {
  if (workspace.read_only) {
    const ended = workspace.subscription_status === "trial_ended";
    return { kind: "readOnly", trialEnd: ended ? (workspace.trial_ends_at ?? null) : null };
  }
  if (workspace.subscription_status === "past_due") return { kind: "pastDue" };
  const photos = workspace.photos_awaiting_deletion ?? 0;
  if (photos > 0) return { kind: "photos", count: photos };
  if (
    workspace.subscription_status === "trialing" &&
    !workspace.subscribed &&
    workspace.trial_ends_at
  ) {
    const days = daysLeft(workspace.trial_ends_at, workspace.timezone, now);
    if (days <= TRIAL_WARNING_DAYS) return { kind: "trial", days };
  }
  return null;
}
